import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations } from '../data/mock';

const LanguageContext = createContext();

// Storage key for language preference
const LANGUAGE_STORAGE_KEY = 'sae_language';

export const LanguageProvider = ({ children }) => {
  // Initialize from localStorage or default to 'en'
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return saved && ['fr', 'en', 'ar'].includes(saved) ? saved : 'en';
  });

  // Persist language choice to localStorage
  useEffect(() => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }, [language]);

  // Set language with page reload (needed for Tawk.to chat widget)
  const setLanguage = useCallback((newLang) => {
    if (['fr', 'en', 'ar'].includes(newLang) && newLang !== language) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
      window.location.reload();
    }
  }, [language]);

  // Alias for setLanguage - also reloads page for chat widget
  const changeLanguage = useCallback((lang) => {
    if (['fr', 'en', 'ar'].includes(lang) && lang !== language) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      window.location.reload();
    }
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    // Fallback to English if translation not found
    if (!value && language !== 'en') {
      value = translations['en'];
      for (const k of keys) {
        value = value?.[k];
      }
    }
    return value || key;
  };

  // Cycle through languages: en -> fr -> ar -> en
  const toggleLanguage = useCallback(() => {
    const newLang = language === 'en' ? 'fr' : language === 'fr' ? 'ar' : 'en';
    setLanguage(newLang);
  }, [language, setLanguage]);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      changeLanguage, 
      toggleLanguage, 
      t 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
